import { convertTimetoString } from "../../../helperFunction";
import css from "../Approvals/css/view-modal.module.css";
import EachPersonalField from "./EachPersonField";
import { ChangeEvent, useContext, useState } from "react";
import Checkbox from "@mui/material/Checkbox";
import { States, Zone, Countries, PersonalDetailsData } from "./type";
import DocumentBar from "./DocumentBar";
import apiCall from "../../../utils/apiCall";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { NotificationContext } from "../../../providers/notification";
import Button from "../../../customs/Button";

interface LGAs {
  LGAId: number;
  LGAName: string;
  StateId: number;
  State: State;
}

interface State {
  StateId: number;
  StateName: string;
  CountryId: number;
  Country?: any;
  Id: number;
  CreatedBy?: any;
  ModifiedBy?: any;
  UserIpAddress?: any;
  CreateDate: string;
  LastModified: string;
  ActiveStatus: boolean;
  IsApprovalRequired?: any;
  RouteId?: any;
  ApprovalTypeId?: any;
  IsDeleted: boolean;
}

interface PersonalDetailsProps {
  data?: PersonalDetailsData;
  state?: States[];
  country?: Countries[];
  lga?: LGAs[];
  zones?: Zone[];
}

interface Payload {
  ApplicantId: string;
  FirstName: string;
  LastName: string;
  MiddleName: string;
  Email: string;
  PhoneNumber: string;
}

const resolveState = (states: States[], stateId: number) => {
  const state = states?.filter((state) => state.StateId === stateId);
  return state[0]?.StateName || "N/A";
};

const resolvelLGA = (lgas: LGAs[], lgaId: number) => {
  const lga = lgas?.filter((lga) => lga.LGAId === lgaId);
  return lga[0]?.LGAName || "N/A";
};

const resolveZone = (zones: Zone[], zoneId: number) => {
  const zone = zones?.filter((zone) => zone.ZoneId === zoneId);
  return zone[0]?.ZoneName || "N/A";
};

const PersonalDetails: React.FC<PersonalDetailsProps> = ({
  data = {} as PersonalDetailsData,
  state = [],
  lga = [],
  country = [],
  zones = [],
}) => {
  const [formData, setFormData] = useState({
    firstName: data?.FirstName || "",
    middleName: data?.MiddleName || "",
    surname: data?.LastName || "",
    email: data?.Email || "",
    phone: data?.PhoneNumber || "",
  });

  const { showNotification } = useContext(NotificationContext);
  const queryClient = useQueryClient();

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const listOFDisability =
    data?.Disablity?.split("/").map((item, index) => (
      <div key={index}>{item}</div>
    )) || [];

  const url = `/Applicant/UpdateApplicantBiodataByApplicantId?ApplicantId=${data?.ApplicantId}`;

  const updateBioData = async (payload: Payload) => {
    return await apiCall().post(url, payload);
  };

  const updateBioDataMutation = useMutation(["update-bio-data"], updateBioData);

  const updateBioDataHandler = async () => {
    const payload: Payload = {
      ApplicantId: data?.ApplicantId,
      FirstName: formData?.firstName,
      LastName: formData?.surname,
      MiddleName: formData?.middleName,
      Email: formData?.email,
      PhoneNumber: formData?.phone,
    };

    try {
      await updateBioDataMutation.mutateAsync(payload, {
        onSuccess: () => {
          showNotification({
            type: "success",
            message: "Details has been successfully updated",
          });

          queryClient.refetchQueries(["get-all-personal-details"]);
          queryClient.refetchQueries(["get-approval"]);
        },
      });
    } catch (error: any) {
      showNotification({
        type: "error",
        message: error?.response?.data?.Message || error?.message,
      });
    }
  };

  return (
    <div className={`${css.peDiv} ${css.personalD}`}>
      <p style={{ marginTop: "32px" }} className={css.title}>
        Personal Details
      </p>

      <div style={{ overflowX: "auto" }}>
        {data?.TitleId == "Others" ? (
          <EachPersonalField
            name="title"
            label="Title"
            value={data?.OtherTitle}
            isDisabled
          />
        ) : (
          <EachPersonalField
            name="title"
            label="Title"
            value={data?.TitleId}
            isDisabled
          />
        )}
        <EachPersonalField
          name="firstName"
          label="First Name"
          value={formData?.firstName}
          onChange={handleChange}
        />
        <EachPersonalField
          name="middleName"
          label="Middle Name"
          value={formData?.middleName}
          onChange={handleChange}
        />
        <EachPersonalField
          name="surname"
          label="Surname"
          value={formData?.surname}
          onChange={handleChange}
        />
        <EachPersonalField
          name="phone"
          label="Phone Number"
          value={formData?.phone}
          onChange={handleChange}
        />
        <EachPersonalField
          name="whatsApp"
          label="Whatsapp Numbers"
          value={data?.WhatsAppNumber}
          isDisabled
        />
        <EachPersonalField
          name="dob"
          label="Date of Birth"
          value={convertTimetoString(data?.DateBirth, 0)}
          isDisabled
        />
        <EachPersonalField
          name="email"
          label="Email Address"
          value={formData?.email}
          onChange={handleChange}
        />
        <EachPersonalField
          name="stateOfOrigin"
          label="State of Origin"
          value={resolveState(state, data?.StateOriginId || 0)}
          isDisabled
        />
        <EachPersonalField
          name="lga"
          label="LGA"
          value={resolvelLGA(lga, data?.LGAId || 0)}
          isDisabled
        />
        <EachPersonalField
          name="StatePOE"
          label="State (Place of Employment)"
          value={resolveState(state, data?.StateId || 0)}
          isDisabled
        />
        <EachPersonalField
          name="zone"
          label="Zone"
          value={resolveZone(zones, data?.ZoneId || 0)}
          isDisabled
        />
        {/* <EachPersonalField label="Country" value={resolveCountry(country, data?.CountryId || 0)} /> */}
        <EachPersonalField
          name="residentialAddress"
          label="Residential Address"
          value={data?.ResidentialAddress}
          isTextArea
          isDisabled
        />
        <EachPersonalField
          name="contactAddress"
          label="Contact Address"
          value={data?.ContactAddress}
          isTextArea
          isDisabled
        />
        <EachPersonalField
          name="gender"
          label="Gender"
          value={data?.Gender == 0 ? "N/A" : data?.Gender}
          isDisabled
        />
        <EachPersonalField
          name="maritalStatus"
          label="Marital status"
          value={data?.MaritalStatusId == 0 ? "N/A" : data?.MaritalStatusId}
          isDisabled
        />
      </div>

      <div className={css.changeDiv}>
        <div>
          <span>Do you consider yourself to have a disablity?</span>
          <div>
            <label>
              <Checkbox checked={data?.HasDisablity} /> Yes
            </label>
            <label>
              <Checkbox
                sx={{ accentColor: "#8DC467" }}
                checked={!data?.HasDisablity}
              />{" "}
              No
            </label>
          </div>
        </div>
        {data?.HasDisablity && (
          <EachPersonalField
            style={{ alignItems: "start" }}
            label={"Disablit" + (listOFDisability?.length > 1 ? "ies" : "y")}
            hasDisability
            value={
              <div style={{ display: "flex", flexDirection: "column" }}>
                {listOFDisability}
                <div style={{ paddingTop: "5px" }}>
                  {data?.OtherDisability && (
                    <>
                      <b>Others:</b> {data?.OtherDisability}
                    </>
                  )}
                </div>
              </div>
            }
          />
        )}

        <div>
          <span>Have you done change of Name</span>
          <div>
            <label>
              <Checkbox checked={data?.IsChangeofName} /> Yes
            </label>
            <label>
              <Checkbox checked={!data?.IsChangeofName} /> No
            </label>
          </div>
        </div>

        {data?.IsChangeofName && (
          <DocumentBar
            hideScoreIntegration={false}
            docName={"Change of Name Certificate"}
            documentUrl={data?.UploadChangeofNameUrl}
            onChange={() => {}}
          />
        )}

        {data?.NYSCDocumentUrl && (
          <DocumentBar
            hideScoreIntegration={false}
            docName={"Call-up Letter"}
            documentUrl={data?.NYSCDocumentUrl}
            onChange={() => {}}
          />
        )}
      </div>

      <div className={css.btnWrapper}>
        <Button
          className="button"
          text={updateBioDataMutation?.isLoading ? "Updating..." : "Update"}
          onClick={updateBioDataHandler}
          disabled={updateBioDataMutation?.isLoading}
        />
      </div>
    </div>
  );
};

export default PersonalDetails;
